import React from "react";
import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Building2, CreditCard, Coins, Cpu, Server, Palette,
  Search, Wallet, BarChart3, Settings as SettingsIcon, ScrollText, Route, LogOut, ArrowLeft, Shield, ToggleLeft, FileText, Scale, Paintbrush, Plug,
} from "lucide-react";
import { useAuth } from "@/context/AuthProvider";
import { useT } from "@/i18n";
import ThemeToggle from "@/components/common/ThemeToggle";
import AdminPasswordPrompt from "@/admin/AdminPasswordPrompt";

const NAV = [
  { to: "/admin", end: true, icon: LayoutDashboard, key: "dashboard", id: "dashboard" },
  { to: "/admin/users", icon: Users, key: "users", id: "users" },
  { to: "/admin/merchants", icon: Building2, key: "merchants", id: "merchants" },
  { to: "/admin/plans", icon: CreditCard, key: "plans", id: "plans" },
  { to: "/admin/credit-packs", icon: Coins, key: "creditPacks", id: "credit-packs" },
  { to: "/admin/ai-models", icon: Cpu, key: "aiModels", id: "ai-models" },
  { to: "/admin/prompts", icon: FileText, key: "prompts", id: "prompts" },
  { to: "/admin/ai-providers", icon: Server, key: "aiProviders", id: "ai-providers" },
  { to: "/admin/ai-router", icon: Route, key: "aiRouter", id: "ai-router" },
  { to: "/admin/cost", icon: BarChart3, key: "cost", id: "cost" },
  { to: "/admin/credits", icon: Wallet, key: "credits", id: "credits" },
  { to: "/admin/appearance", icon: Paintbrush, key: "appearance", id: "appearance" },
  { to: "/admin/integrations/shopify", icon: Plug, key: "shopify", id: "integrations-shopify" },
  { to: "/admin/legal", icon: Scale, key: "legal", id: "legal" },
  { to: "/admin/billing", icon: CreditCard, key: "billing", id: "billing" },
  { to: "/admin/feature-flags", icon: ToggleLeft, key: "featureFlags", id: "feature-flags" },
  { to: "/admin/audit", icon: ScrollText, key: "audit", id: "audit" },
  { to: "/admin/settings", icon: SettingsIcon, key: "settings", id: "settings" },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const t = useT();
  const navigate = useNavigate();

  if (loading || user === null) return <div className="flex min-h-screen items-center justify-center bg-background"><div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-border bg-card lg:flex">
        <div className="flex h-16 items-center gap-2 border-b border-border px-5">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-brand text-white"><Shield className="h-4 w-4" /></span>
          <span className="font-display font-extrabold tracking-tighter">{t("admin.console")}</span>
        </div>
        <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto p-3">
          {NAV.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} data-testid={`admin-nav-${it.id}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"}`}>
              <it.icon className="h-4 w-4" />{t(`admin.nav.${it.key}`)}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-3">
          <button onClick={() => navigate("/dashboard")} data-testid="admin-back-to-app" className="mb-1 flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-secondary/60 hover:text-foreground">
            <ArrowLeft className="h-4 w-4" />{t("admin.backToApp")}
          </button>
          <button onClick={async () => { await logout(); navigate("/"); }} data-testid="admin-logout" className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-secondary/60 hover:text-foreground">
            <LogOut className="h-4 w-4" />{t("admin.logout")}
          </button>
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col lg:pl-64">
        <header className="glass sticky top-0 z-40 flex h-16 items-center justify-between border-b border-border px-6">
          <span className="text-sm text-muted-foreground">{t("admin.signedInAs")} <span className="font-medium text-foreground">{user.email}</span> · <span className="font-mono text-brand">{user.role}</span></span>
          <ThemeToggle />
        </header>
        <main className="flex-1 overflow-x-hidden p-6 lg:p-8"><Outlet /></main>
      </div>
      <AdminPasswordPrompt />
    </div>
  );
}
